/**
 * GRC Copilot Service API
 * React Query hooks for the AI Copilot microservice
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { get, post, put, patch, del } from '@/lib/api/client'

// W24: shim to keep existing apiClient.get/post/<T>() signature while auto-unwrapping
// the response body (matches pattern used by helper-based services).
const apiClient = {
  get: <T = any>(url: string, config?: any) => get<T>(url, config),
  post: <T = any>(url: string, data?: any, config?: any) => post<T>(url, data, config),
  put: <T = any>(url: string, data?: any, config?: any) => put<T>(url, data, config),
  patch: <T = any>(url: string, data?: any, config?: any) => patch<T>(url, data, config),
  delete: <T = any>(url: string, config?: any) => del<T>(url, config),
}
import { keycloak, getTenantId } from '@/lib/auth/keycloak'
import { resolveApiUrl } from '@/lib/api/config'

// ── Types ────────────────────────────────────────────────────────────────────

export interface CopilotMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  metadata?: Record<string, unknown>
  feedback?: 'positive' | 'negative'
}

export interface CopilotChatRequest {
  message: string
  moduleContext: string
  pageContext: string
  conversationHistory?: CopilotMessage[]
  entityData?: Record<string, unknown>
  page_data?: Record<string, unknown>
}

export interface CopilotChatResponse {
  id: string
  content: string
  suggestions?: string[]
  sources?: { title: string; url?: string }[]
}

export interface CopilotSummaryRequest {
  context: string
  data: unknown
}

export interface CopilotSummaryResponse {
  keyInsight: string
  trend: string
  recommendedAction: string
  generatedAt: string
}

export interface CopilotSearchResult {
  id: string
  title: string
  category: 'risk' | 'control' | 'policy' | 'assessment' | 'compliance' | 'evidence' | 'framework' | 'feature' | 'other'
  description: string
  relevance: number
  module: string
  url?: string
  icon?: string
}

// ── Feature Navigation Types ─────────────────────────────────────────────────

export interface FeatureMatch {
  feature_id: string
  title_fr: string
  route: string
  module: string
  score: number
  icon?: string
}

interface FeatureManifestEntry {
  feature_id: string
  title_fr: string
  route: string
  module: string
  icon?: string
  keywords: string[]
}

// Local fuzzy matcher used as fallback when navigate endpoint is unreachable
let _featureManifestCache: FeatureManifestEntry[] | null = null

async function _loadFeatureManifest(): Promise<FeatureManifestEntry[]> {
  if (_featureManifestCache) return _featureManifestCache
  try {
    const res = await fetch('/feature-manifest.json')
    if (!res.ok) return []
    const data = await res.json()
    _featureManifestCache = data.features ?? []
    return _featureManifestCache!
  } catch {
    return []
  }
}

function _localFuzzySearch(query: string, entries: FeatureManifestEntry[]): FeatureMatch[] {
  const words = query
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/\s+/)
    .filter(Boolean)

  return entries
    .map((entry) => {
      const haystack = `${entry.title_fr} ${entry.keywords.join(' ')} ${entry.module}`
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
      const matched = words.filter((w) => haystack.includes(w)).length
      const score = words.length > 0 ? matched / words.length : 0
      return {
        feature_id: entry.feature_id,
        title_fr: entry.title_fr,
        route: entry.route,
        module: entry.module,
        icon: entry.icon,
        score,
      } as FeatureMatch
    })
    .filter((m) => m.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5)
}

export interface CopilotSearchResponse {
  results: CopilotSearchResult[]
  query: string
  totalResults: number
}

export interface DocumentAnalysisResult {
  keyClauses: { clause: string; section: string; importance: 'high' | 'medium' | 'low' }[]
  mappedControls: { controlId: string; controlName: string; mapping: string }[]
  identifiedObligations: { obligation: string; dueDate?: string; responsible?: string }[]
  summary: string
  documentType: string
}

export interface WhatIfScenario {
  description: string
  affectedParameters: Record<string, { current: number; proposed: number }>
}

export interface WhatIfResult {
  scenarioId: string
  description: string
  impacts: {
    metric: string
    before: number
    after: number
    delta: number
    severity: 'critical' | 'high' | 'medium' | 'low'
  }[]
  overallRisk: 'increased' | 'decreased' | 'stable'
  recommendations: string[]
}

export interface ActionRecommendation {
  id: string
  title: string
  description: string
  module: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  impactEstimate: string
  dueDate: string
  effort: 'low' | 'medium' | 'high'
}

export interface AnomalyDetection {
  id: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  description: string
  affectedMetric: string
  currentValue: number
  expectedValue: number
  deviation: number
  suggestedInvestigation: string
  detectedAt: string
}

// ── RAG / Knowledge semantic-search types ─────────────────────────────────────

/** One ranked hit from the knowledge-service semantic index. */
export interface KnowledgeSearchResult {
  entity_type: string
  entity_id: string
  title: string
  snippet: string
  score: number
  url: string
  metadata?: Record<string, unknown>
}

/** A grounded citation returned by /analyze or /advise. */
export interface KnowledgeCitation {
  entity_type: string
  entity_id: string
  title: string
  /** 1-based citation number referenced in the answer text. */
  n: number
  /** Optional deep-link (present when the backend can resolve one). */
  url?: string
  /** TRUST layer (subsystem J): source scope — 'permanent' | 'session'. */
  scope?: string
  /** Provenance (subsystem L): source doc id / uploader / ingestion time. */
  source_doc_id?: string
  uploader?: string
  ingested_at?: string
}

/** Grounded RAG answer for /knowledge/entities/analyze. */
export interface KnowledgeAnalysis {
  answer: string
  citations: KnowledgeCitation[]
  grounded: boolean
}

/** A single advisory item from /ai/copilot/advise. */
export interface CopilotAdvice {
  title: string
  detail: string
  priority: string
}

export interface CopilotAdviseResult {
  advice: CopilotAdvice[]
  grounded: boolean
  citations: KnowledgeCitation[]
}

/** Describes one field of a form so the LLM can fill it. */
export interface FormSchemaField {
  name: string
  label: string
  type: string
  enum?: string[]
  required?: boolean
}

export interface FillFormResult {
  fields: Record<string, unknown>
  confidence?: number
  notes?: string
  unfilled?: string[]
}

export interface ExtractFileResult {
  document_text: string
  fields?: Record<string, unknown>
  summary?: string
}

// ── Copilot-v2 types (suggest-links / generate-record / inbox / reg-impact) ────

/** One scored link suggestion returned by /ai/copilot/suggest-links. */
export interface CopilotLinkSuggestion {
  entity_type: string
  entity_id: string
  title: string
  score: number
  rationale?: string
  url?: string
}

export interface SuggestLinksResponse {
  source?: Record<string, unknown>
  suggestions: CopilotLinkSuggestion[]
  model?: string
}

/** Complete generated draft from /ai/copilot/generate-record. */
export interface GenerateRecordResult {
  fields: Record<string, unknown>
  body_markdown: string
  sections?: Array<{ title?: string; content?: string }> | Record<string, unknown>
  confidence?: number
  citations: KnowledgeCitation[]
  grounded: boolean
  unfilled?: string[]
  model?: string
}

/** One actionable inbox card from /ai/copilot/inbox. */
export interface CopilotInboxItem {
  id: string
  type: string
  title: string
  detail?: string
  priority: string
  module: string
  grounded?: boolean
  cta?: { label: string; action: { op: 'navigate'; to: string } }
}

export interface CopilotInboxResponse {
  items: CopilotInboxItem[]
  generated_at?: string
  model?: string
}

/** One impacted entity in a reg-impact analysis. */
export interface RegImpactEntity {
  entity_id: string
  title: string
  score?: number
  impact?: string
  why?: string
}

export interface RegImpactActionPlanItem {
  title: string
  priority?: string
  entity_ref?: string
  effort?: string
}

export interface RegImpactResult {
  summary: string
  impacted: {
    policy?: RegImpactEntity[]
    control?: RegImpactEntity[]
    risk?: RegImpactEntity[]
    obligation?: RegImpactEntity[]
    [k: string]: RegImpactEntity[] | undefined
  }
  action_plan: RegImpactActionPlanItem[]
  citations: KnowledgeCitation[]
  grounded: boolean
  model?: string
}

// ── F8 Agentic tool-planner types (POST /ai/copilot/agent/plan) ───────────────

/** One field descriptor sent to the planner so it knows what a form can hold. */
export interface AgentAvailableFormField {
  name: string
  label: string
  type: string
  enum?: string[]
}

/** A create-form schema the agent may target with a `fillForm` step. */
export interface AgentAvailableForm {
  form_id: string
  entity_type: string
  fields: AgentAvailableFormField[]
}

/** One ordered step of an agentic plan. `args` support `{{stepN.field}}` refs. */
export interface AgentPlanStep {
  step: number
  op:
    | 'navigate'
    | 'fillForm'
    | 'search'
    | 'findSimilar'
    | 'analyze'
    | 'suggestLinks'
    | 'generateRecord'
    | 'createRecord'
    | 'linkRecords'
    | string
  args: Record<string, unknown>
  side_effect: 'read' | 'write' | 'destructive' | 'outward' | string
  confirm: boolean
  /** Optional human label some planner versions include. */
  label?: string
}

export interface AgentPlanRequest {
  goal: string
  module?: string
  page_context?: string
  available_forms?: AgentAvailableForm[]
  available_tools?: string[]
  max_steps?: number
}

export interface AgentPlanResponse {
  assistant_message: string
  plan: AgentPlanStep[]
  requires_confirmation: boolean
  grounded: boolean
  model?: string
}

// ── Copilot-v3 types (import-records / meeting-actions / categorize / framework-gap / answer-questionnaire) ──

/** One extracted record from POST /ai/copilot/import-records. */
export interface ImportedRecord {
  fields: Record<string, unknown>
  confidence?: number
  source_ref?: string
}

export interface ImportRecordsResult {
  records: ImportedRecord[]
  count: number
  notes?: string
  unfilled_summary?: string
}

export interface MeetingDecision {
  text: string
}

export interface MeetingActionItem {
  title: string
  owner?: string
  due_hint?: string
  priority?: string
}

export interface MeetingActionsResult {
  summary: string
  decisions: MeetingDecision[]
  action_items: MeetingActionItem[]
  risks_flagged: string[]
}

export interface CategorizeResult {
  category: string
  subcategory?: string
  tags: string[]
  confidence?: number
  rationale?: string
}

/** One covered requirement (with a linkable evidence entity) in a framework-gap analysis. */
export interface FrameworkGapCovered {
  requirement: string
  /** The backend may return a bare string ref OR a structured entity object. */
  evidence_entity_ref?: string | { entity_type?: string; entity_id?: string; title?: string }
}

/** One uncovered requirement (a gap) in a framework-gap analysis. */
export interface FrameworkGapItem {
  requirement: string
  severity?: string
  recommendation?: string
}

export interface FrameworkGapResult {
  framework: string
  coverage_pct: number
  covered: FrameworkGapCovered[]
  gaps: FrameworkGapItem[]
  summary: string
  grounded: boolean
}

/** One grounded source cited for a drafted questionnaire answer. */
export interface QuestionnaireAnswerSource {
  entity_type: string
  entity_id: string
  title: string
}

export interface QuestionnaireAnswer {
  question: string
  answer: string
  confidence?: number
  sources: QuestionnaireAnswerSource[]
  status?: string
}

export interface AnswerQuestionnaireResult {
  answers: QuestionnaireAnswer[]
  answered_count: number
}

// ── API Methods ──────────────────────────────────────────────────────────────

// The shared axios client uses a 30s timeout, but grounded RAG synthesis
// (/knowledge/entities/analyze, /ai/copilot/advise, form-fill) invokes a real
// LLM and can run ~45-60s. Without a longer per-call timeout the request is
// aborted at 30s (net::ERR_ABORTED) and the answer never renders.
const RAG_LLM_TIMEOUT = 120_000
// Vector search/similar are usually fast but can be cold; give modest headroom.
const RAG_SEARCH_TIMEOUT = 60_000
// #copilot-hangs — the meeting-actions / framework-gap synthesis endpoints are now
// BACKEND-bounded (~150s asyncio.wait_for + a friendly fallback that returns HTTP
// 200). Give the client a slightly larger budget than that backend cap so we
// always RENDER the backend's bounded response, then abort as a last-resort safety
// net (still < the 300s nginx per-location budget) so the spinner can never spin
// forever. The callers also surface a friendly toast on abort/error.
const COPILOT_SYNTH_TIMEOUT = 170_000
// #copilot-latency — the agentic planner (/ai/copilot/agent/plan) and AI
// form-fill (/ai/copilot/fill-form) now prefer a FAST commercial model in the
// backend (route_llm_for_user), so they typically complete in seconds. But when
// a tenant is on OSS provider-mode (or billing routing is unavailable), they
// fall back to the slow OSS model (110-160s) — which used to abort at the 120s
// RAG_LLM_TIMEOUT and surface "…a échoué". Give these two the same proven budget
// as the other bounded synthesis endpoints (meeting-actions / framework-gap) so
// the backend's response always renders instead of being cut off. Still < the
// 300s nginx per-location budget.
const COPILOT_AGENT_TIMEOUT = COPILOT_SYNTH_TIMEOUT

export const copilotApi = {
  chat: async (request: CopilotChatRequest): Promise<CopilotChatResponse> => {
    const response = await apiClient.post('/ai/copilot/chat', request)
    return response.data
  },

  chatStream: async (
    message: string,
    context: {
      moduleContext: string
      pageContext: string
      entityData?: Record<string, unknown>
      page_data?: Record<string, unknown>
      /** Companion Assistant/Agent mode (COPILOT_MASTER_PLAN subsystem H).
       *  Additive hint — backends that predate the autonomy policy ignore it. */
      mode?: 'assistant' | 'agent'
    },
    conversationHistory: CopilotMessage[],
    onToken: (token: string) => void,
    onDone: () => void,
    onError: (error: string) => void,
  ) => {
    const response = await fetch(resolveApiUrl('/ai/copilot/chat/stream'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(keycloak.token ? { Authorization: `Bearer ${keycloak.token}` } : {}),
        'X-Tenant-ID': getTenantId() || '',
      },
      body: JSON.stringify({
        message,
        moduleContext: context.moduleContext,
        pageContext: context.pageContext,
        entityData: context.entityData || null,
        page_data: context.page_data || null,
        ...(context.mode ? { mode: context.mode, autonomy: context.mode } : {}),
        conversationHistory: conversationHistory.map((m) => ({ role: m.role, content: m.content })),
      }),
    })

    if (!response.ok) throw new Error('Stream failed')

    const reader = response.body?.getReader()
    const decoder = new TextDecoder()

    if (!reader) throw new Error('No reader')

    let buffer = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6))
            if (data.type === 'token') onToken(data.content)
            else if (data.type === 'done') onDone()
            else if (data.type === 'error') onError(data.content)
          } catch {
            // Ignore malformed SSE lines
          }
        }
      }
    }
  },

  summarize: async (request: CopilotSummaryRequest): Promise<CopilotSummaryResponse> => {
    const response = await apiClient.post('/ai/copilot/summarize', request)
    return response.data
  },

  search: async (query: string): Promise<CopilotSearchResponse> => {
    const response = await apiClient.get('/ai/copilot/search', { params: { q: query } })
    return response.data
  },

  analyzeDocument: async (
    file: File,
    moduleContext: string,
    onProgress?: (progress: number) => void
  ): Promise<DocumentAnalysisResult> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('moduleContext', moduleContext)

    const response = await apiClient.post('/ai/copilot/analyze-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })
    return response.data
  },

  whatIf: async (
    scenario: WhatIfScenario,
    moduleContext: string,
    currentData: unknown
  ): Promise<WhatIfResult> => {
    const response = await apiClient.post('/ai/copilot/what-if', {
      scenario,
      moduleContext,
      currentData,
    })
    return response.data
  },

  getRecommendations: async (
    moduleContext: string,
    tenantData?: unknown
  ): Promise<ActionRecommendation[]> => {
    const response = await apiClient.get('/ai/copilot/recommendations', {
      params: { moduleContext, ...(tenantData ? { tenantData: JSON.stringify(tenantData) } : {}) },
    })
    // Backend now returns a rich object { recommendations, items, ... }; older
    // shape was a bare array. Normalize so ActionRecommender always gets an array.
    const d = response.data as any
    if (Array.isArray(d)) return d
    return (d?.recommendations ?? d?.items ?? []) as ActionRecommendation[]
  },

  getAnomalies: async (
    moduleContext: string,
    metrics?: unknown
  ): Promise<AnomalyDetection[]> => {
    const response = await apiClient.get('/ai/copilot/anomalies', {
      params: { moduleContext, ...(metrics ? { metrics: JSON.stringify(metrics) } : {}) },
    })
    const d = response.data as any
    if (Array.isArray(d)) return d
    return (d?.anomalies ?? d?.items ?? []) as AnomalyDetection[]
  },

  navigateToFeature: async (query: string, portal = 'client'): Promise<FeatureMatch[]> => {
    try {
      const response = await fetch(resolveApiUrl('/copilot/navigate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(keycloak.token ? { Authorization: `Bearer ${keycloak.token}` } : {}),
          'X-Tenant-ID': getTenantId() || '',
        },
        body: JSON.stringify({ query, portal }),
        signal: AbortSignal.timeout(3000),
      })
      if (!response.ok) throw new Error('Navigate endpoint failed')
      const data = await response.json()
      const results: FeatureMatch[] = data.results ?? data ?? []
      if (results.length > 0) return results
      // Backend returned empty — fall through to local search
      throw new Error('Empty results from backend')
    } catch {
      // Fallback: local fuzzy search on embedded feature-manifest.json
      const manifest = await _loadFeatureManifest()
      return _localFuzzySearch(query, manifest)
    }
  },

  // ── RAG / semantic search (knowledge-service) ───────────────────────────────

  /** Cross-entity semantic search. Returns ranked hits grouped client-side. */
  semanticSearch: async (
    query: string,
    opts?: { types?: string[]; limit?: number },
  ): Promise<KnowledgeSearchResult[]> => {
    // The knowledge-service returns `{ query, hits: [...] }`; older/mocked
    // variants returned a bare array or `{ results: [...] }`. Accept all three.
    const body = await post<
      KnowledgeSearchResult[] | { results?: KnowledgeSearchResult[]; hits?: KnowledgeSearchResult[] }
    >(
      '/knowledge/entities/search',
      { query, types: opts?.types, limit: opts?.limit ?? 20 },
      { timeout: RAG_SEARCH_TIMEOUT },
    )
    return Array.isArray(body) ? body : (body?.hits ?? body?.results ?? [])
  },

  /** Find records semantically similar to a given entity. */
  findSimilar: async (
    entityType: string,
    entityId: string,
    limit = 8,
  ): Promise<KnowledgeSearchResult[]> => {
    const body = await get<
      KnowledgeSearchResult[] | { results?: KnowledgeSearchResult[]; hits?: KnowledgeSearchResult[] }
    >(
      '/knowledge/entities/similar',
      { params: { entity_type: entityType, entity_id: entityId, limit }, timeout: RAG_SEARCH_TIMEOUT },
    )
    return Array.isArray(body) ? body : (body?.hits ?? body?.results ?? [])
  },

  /** Grounded RAG Q&A over the knowledge base (optionally scoped to an entity). */
  ragAnalyze: async (req: {
    question: string
    types?: string[]
    entity_type?: string
    entity_id?: string
    /** TRUST layer (subsystem J): include the caller's session-scoped docs. */
    session_id?: string
  }): Promise<KnowledgeAnalysis> => {
    // Grounded synthesis runs a real LLM call over retrieved context and can
    // take well beyond the shared axios 30s default; give it a generous window
    // so the answer renders instead of aborting with net::ERR_ABORTED.
    return post<KnowledgeAnalysis>('/knowledge/entities/analyze', req, { timeout: RAG_LLM_TIMEOUT })
  },

  /** Context-aware advisory (advice[] + citations) for a module/entity. */
  advise: async (req: {
    module?: string
    entity_type?: string
    entity_id?: string
    question?: string
  }): Promise<CopilotAdviseResult> => {
    return post<CopilotAdviseResult>('/ai/copilot/advise', req, { timeout: RAG_LLM_TIMEOUT })
  },

  /** Fill a form from a natural-language prompt and/or extracted document text. */
  fillForm: async (req: {
    form_schema: FormSchemaField[]
    prompt?: string
    document_text?: string
    module?: string
  }): Promise<FillFormResult> => {
    // NOTE: no X-User-ID header here — the api-gateway CORS allow-list does not
    // include it (preflight would fail). The backend derives the user from the
    // JWT `sub` claim for user-aware LLM routing (user_id_from_headers).
    return post<FillFormResult>('/ai/copilot/fill-form', req, {
      timeout: COPILOT_AGENT_TIMEOUT,
    })
  },

  /** Upload a document; the backend extracts text (+ optionally fills the form). */
  extractFile: async (
    file: File,
    formSchema?: FormSchemaField[],
    module?: string,
  ): Promise<ExtractFileResult> => {
    const fd = new FormData()
    fd.append('file', file)
    if (formSchema) fd.append('form_schema', JSON.stringify(formSchema))
    if (module) fd.append('module', module)
    return post<ExtractFileResult>('/ai/copilot/extract-file', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: RAG_LLM_TIMEOUT,
    })
  },

  // ── Copilot-v2: contextual link suggestions ─────────────────────────────────

  /** Suggest links (e.g. controls for a risk) scored + rationale'd. Normalizes
   *  the copilot-v2 `suggestions` array (vs knowledge-service `hits`). */
  suggestLinks: async (req: {
    entity_type: string
    entity_id: string
    target_type: string
    module?: string
    limit?: number
    with_rationale?: boolean
  }): Promise<SuggestLinksResponse> => {
    const body = await post<
      | SuggestLinksResponse
      | { suggestions?: CopilotLinkSuggestion[]; results?: CopilotLinkSuggestion[]; hits?: CopilotLinkSuggestion[]; source?: Record<string, unknown>; model?: string }
      | CopilotLinkSuggestion[]
    >(
      '/ai/copilot/suggest-links',
      { with_rationale: true, ...req },
      { timeout: RAG_LLM_TIMEOUT },
    )
    const suggestions = Array.isArray(body)
      ? body
      : (body?.suggestions ?? (body as { results?: CopilotLinkSuggestion[] })?.results ?? (body as { hits?: CopilotLinkSuggestion[] })?.hits ?? [])
    return {
      source: Array.isArray(body) ? undefined : body?.source,
      suggestions,
      model: Array.isArray(body) ? undefined : body?.model,
    }
  },

  // ── Copilot-v2: generate a COMPLETE record draft (fields + long-form body) ───

  generateRecord: async (req: {
    entity_type: string
    module?: string
    prompt: string
    form_schema?: FormSchemaField[]
    ground_with_rag?: boolean
  }): Promise<GenerateRecordResult> => {
    const body = await post<GenerateRecordResult>('/ai/copilot/generate-record', req, {
      timeout: RAG_LLM_TIMEOUT,
    })
    return {
      fields: body?.fields ?? {},
      body_markdown: body?.body_markdown ?? '',
      sections: body?.sections,
      confidence: body?.confidence,
      citations: body?.citations ?? [],
      grounded: body?.grounded ?? false,
      unfilled: body?.unfilled,
      model: body?.model,
    }
  },

  // ── Copilot-v2: "À traiter" inbox of prioritized, actionable items ───────────

  inbox: async (opts?: { module?: string; limit?: number }): Promise<CopilotInboxResponse> => {
    const body = await get<CopilotInboxResponse | CopilotInboxItem[]>('/ai/copilot/inbox', {
      params: { module: opts?.module ?? 'all', limit: opts?.limit ?? 10 },
      timeout: RAG_LLM_TIMEOUT,
    })
    const items = Array.isArray(body) ? body : (body?.items ?? [])
    return {
      items,
      generated_at: Array.isArray(body) ? undefined : body?.generated_at,
      model: Array.isArray(body) ? undefined : body?.model,
    }
  },

  // ── Copilot-v2: regulatory-impact analysis ──────────────────────────────────

  regImpact: async (req: {
    regulation_text: string
    regulation_ref?: string
    scope_types?: string[]
    limit_per_type?: number
  }): Promise<RegImpactResult> => {
    const body = await post<RegImpactResult>('/ai/copilot/reg-impact', req, {
      timeout: RAG_LLM_TIMEOUT,
    })
    return {
      summary: body?.summary ?? '',
      impacted: body?.impacted ?? {},
      action_plan: body?.action_plan ?? [],
      citations: body?.citations ?? [],
      grounded: body?.grounded ?? false,
      model: body?.model,
    }
  },

  // ── F8: agentic tool-planner — turn a NL goal into an ordered plan ──────────

  /** Plan an agentic multi-step task. Real LLM planning → long timeout. */
  agentPlan: async (req: AgentPlanRequest): Promise<AgentPlanResponse> => {
    // NOTE: no X-User-ID header here — the api-gateway CORS allow-list does not
    // include it (preflight would fail). The backend derives the user from the
    // JWT `sub` claim for user-aware LLM routing (fast commercial planning).
    const body = await post<AgentPlanResponse>('/ai/copilot/agent/plan', req, {
      timeout: COPILOT_AGENT_TIMEOUT,
    })
    return {
      assistant_message: body?.assistant_message ?? '',
      plan: Array.isArray(body?.plan) ? body.plan : [],
      requires_confirmation: body?.requires_confirmation ?? true,
      grounded: body?.grounded ?? false,
      model: body?.model,
    }
  },

  // ── Copilot-v3: RPA bulk-import — extract MANY records from a file / text ─────

  /** Extract multiple records (cap 50) from a file/csv/xlsx or pasted text so a
   *  list page can bulk-create them. Multipart when a file is supplied, JSON
   *  otherwise. Normalizes the `records` array defensively. */
  importRecords: async (req: {
    file?: File
    document_text?: string
    entity_type: string
    module?: string
    form_schema?: FormSchemaField[]
  }): Promise<ImportRecordsResult> => {
    let body: ImportRecordsResult
    if (req.file) {
      const fd = new FormData()
      fd.append('file', req.file)
      fd.append('entity_type', req.entity_type)
      if (req.module) fd.append('module', req.module)
      if (req.form_schema) fd.append('form_schema', JSON.stringify(req.form_schema))
      body = await post<ImportRecordsResult>('/ai/copilot/import-records', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: RAG_LLM_TIMEOUT,
      })
    } else {
      body = await post<ImportRecordsResult>(
        '/ai/copilot/import-records',
        {
          document_text: req.document_text ?? '',
          entity_type: req.entity_type,
          module: req.module,
          form_schema: req.form_schema,
        },
        { timeout: RAG_LLM_TIMEOUT },
      )
    }
    const records = Array.isArray(body?.records) ? body.records : []
    return {
      records,
      count: typeof body?.count === 'number' ? body.count : records.length,
      notes: body?.notes,
      unfilled_summary: body?.unfilled_summary,
    }
  },

  // ── Copilot-v3: meeting notes → decisions + action items + flagged risks ─────

  meetingActions: async (req: {
    notes?: string
    document_text?: string
    module?: string
  }): Promise<MeetingActionsResult> => {
    const body = await post<MeetingActionsResult>(
      '/ai/copilot/meeting-actions',
      { notes: req.notes, document_text: req.document_text, module: req.module },
      { timeout: COPILOT_SYNTH_TIMEOUT },
    )
    return {
      summary: body?.summary ?? '',
      decisions: Array.isArray(body?.decisions) ? body.decisions : [],
      action_items: Array.isArray(body?.action_items) ? body.action_items : [],
      risks_flagged: Array.isArray(body?.risks_flagged) ? body.risks_flagged : [],
    }
  },

  // ── Copilot-v3: suggest a category / subcategory / tags for a free text ──────

  categorize: async (req: {
    entity_type: string
    text: string
    taxonomy?: string[]
  }): Promise<CategorizeResult> => {
    const body = await post<CategorizeResult>('/ai/copilot/categorize', req, {
      timeout: RAG_LLM_TIMEOUT,
    })
    return {
      category: body?.category ?? '',
      subcategory: body?.subcategory,
      tags: Array.isArray(body?.tags) ? body.tags : [],
      confidence: body?.confidence,
      rationale: body?.rationale,
    }
  },

  // ── Copilot-v3: regulatory framework gap analysis (grounded on your data) ────

  frameworkGap: async (req: { framework: string; module?: string }): Promise<FrameworkGapResult> => {
    const body = await post<FrameworkGapResult>('/ai/copilot/framework-gap', req, {
      timeout: COPILOT_SYNTH_TIMEOUT,
    })
    return {
      framework: body?.framework ?? req.framework,
      coverage_pct: typeof body?.coverage_pct === 'number' ? body.coverage_pct : 0,
      covered: Array.isArray(body?.covered) ? body.covered : [],
      gaps: Array.isArray(body?.gaps) ? body.gaps : [],
      summary: body?.summary ?? '',
      grounded: body?.grounded ?? false,
    }
  },

  // ── Copilot-v3: auto-answer a security / due-diligence questionnaire ─────────

  answerQuestionnaire: async (req: {
    file?: File
    questions?: string[]
  }): Promise<AnswerQuestionnaireResult> => {
    let body: AnswerQuestionnaireResult
    if (req.file) {
      const fd = new FormData()
      fd.append('file', req.file)
      body = await post<AnswerQuestionnaireResult>('/ai/copilot/answer-questionnaire', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: RAG_LLM_TIMEOUT,
      })
    } else {
      body = await post<AnswerQuestionnaireResult>(
        '/ai/copilot/answer-questionnaire',
        { questions: req.questions ?? [] },
        { timeout: RAG_LLM_TIMEOUT },
      )
    }
    const answers = Array.isArray(body?.answers)
      ? body.answers.map((a) => ({ ...a, sources: Array.isArray(a?.sources) ? a.sources : [] }))
      : []
    return {
      answers,
      answered_count: typeof body?.answered_count === 'number' ? body.answered_count : answers.length,
    }
  },

  submitFeedback: async (
    messageId: string,
    rating: 'positive' | 'negative',
    context: { moduleContext: string; pageContext: string },
    comment?: string,
  ) => {
    const response = await apiClient.post('/ai/copilot/feedback', {
      message_id: messageId,
      rating,
      comment,
      module_context: context.moduleContext,
      page_context: context.pageContext,
    })
    return response.data
  },
}

// ── Query Keys ───────────────────────────────────────────────────────────────

export const copilotKeys = {
  all: ['copilot'] as const,
  chat: () => [...copilotKeys.all, 'chat'] as const,
  summary: (context: string) => [...copilotKeys.all, 'summary', context] as const,
  search: (query: string) => [...copilotKeys.all, 'search', query] as const,
  recommendations: (context: string) => [...copilotKeys.all, 'recommendations', context] as const,
  anomalies: (context: string) => [...copilotKeys.all, 'anomalies', context] as const,
}

// ── React Query Hooks ────────────────────────────────────────────────────────

export function useCopilotChat() {
  return useMutation({
    mutationFn: copilotApi.chat,
  })
}

export function useCopilotSummary(context: string, data: unknown, enabled = true) {
  return useQuery({
    queryKey: copilotKeys.summary(context),
    queryFn: () => copilotApi.summarize({ context, data }),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useCopilotSearch(query: string, enabled = true) {
  return useQuery({
    queryKey: copilotKeys.search(query),
    queryFn: () => copilotApi.search(query),
    enabled: enabled && query.length >= 2,
    staleTime: 30 * 1000, // 30 seconds
  })
}

export function useDocumentAnalysis() {
  return useMutation({
    mutationFn: ({ file, moduleContext }: { file: File; moduleContext: string }) =>
      copilotApi.analyzeDocument(file, moduleContext),
  })
}

export function useWhatIfAnalysis() {
  return useMutation({
    mutationFn: ({
      scenario,
      moduleContext,
      currentData,
    }: {
      scenario: WhatIfScenario
      moduleContext: string
      currentData: unknown
    }) => copilotApi.whatIf(scenario, moduleContext, currentData),
  })
}

export function useCopilotRecommendations(moduleContext: string, tenantData?: unknown, enabled = true) {
  return useQuery({
    queryKey: copilotKeys.recommendations(moduleContext),
    queryFn: () => copilotApi.getRecommendations(moduleContext, tenantData),
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

export function useCopilotAnomalies(moduleContext: string, metrics?: unknown, enabled = true) {
  return useQuery({
    queryKey: copilotKeys.anomalies(moduleContext),
    queryFn: () => copilotApi.getAnomalies(moduleContext, metrics),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// ── RAG / semantic-search hooks ───────────────────────────────────────────────

/** Debounced-friendly semantic search hook (caller should debounce `query`). */
export function useSemanticSearch(
  query: string,
  opts?: { types?: string[]; limit?: number },
  enabled = true,
) {
  return useQuery({
    queryKey: [...copilotKeys.all, 'semantic-search', query, opts?.types ?? null, opts?.limit ?? null] as const,
    queryFn: () => copilotApi.semanticSearch(query, opts),
    enabled: enabled && query.trim().length >= 2,
    staleTime: 30 * 1000,
    retry: false,
  })
}

/** "Find similar" for a detail page. Disabled until both ids are present. */
export function useFindSimilar(
  entityType?: string,
  entityId?: string,
  limit = 8,
  enabled = true,
) {
  return useQuery({
    queryKey: [...copilotKeys.all, 'similar', entityType ?? null, entityId ?? null, limit] as const,
    queryFn: () => copilotApi.findSimilar(entityType as string, entityId as string, limit),
    enabled: enabled && !!entityType && !!entityId,
    staleTime: 60 * 1000,
    retry: false,
  })
}

export function useRagAnalyze() {
  return useMutation({ mutationFn: copilotApi.ragAnalyze })
}

export function useCopilotAdvise() {
  return useMutation({ mutationFn: copilotApi.advise })
}

export function useFillForm() {
  return useMutation({ mutationFn: copilotApi.fillForm })
}

export function useExtractFile() {
  return useMutation({
    mutationFn: ({
      file,
      formSchema,
      module,
    }: {
      file: File
      formSchema?: FormSchemaField[]
      module?: string
    }) => copilotApi.extractFile(file, formSchema, module),
  })
}

// ── Copilot-v2 hooks ──────────────────────────────────────────────────────────

/** Scored link suggestions for a source entity → target type. Disabled until
 *  both the source id and the target type are present. */
export function useSuggestLinks(
  req: { entity_type?: string; entity_id?: string; target_type?: string; module?: string; limit?: number },
  enabled = true,
) {
  return useQuery({
    queryKey: [...copilotKeys.all, 'suggest-links', req.entity_type ?? null, req.entity_id ?? null, req.target_type ?? null, req.limit ?? null] as const,
    queryFn: () =>
      copilotApi.suggestLinks({
        entity_type: req.entity_type as string,
        entity_id: req.entity_id as string,
        target_type: req.target_type as string,
        module: req.module,
        limit: req.limit,
        with_rationale: true,
      }),
    enabled: enabled && !!req.entity_type && !!req.entity_id && !!req.target_type,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function useGenerateRecord() {
  return useMutation({ mutationFn: copilotApi.generateRecord })
}

/** "À traiter" inbox — auto-fetched, refreshed on a modest interval. */
export function useCopilotInbox(opts?: { module?: string; limit?: number }, enabled = true) {
  return useQuery({
    queryKey: [...copilotKeys.all, 'inbox', opts?.module ?? 'all', opts?.limit ?? 10] as const,
    queryFn: () => copilotApi.inbox(opts),
    enabled,
    staleTime: 2 * 60 * 1000,
    retry: false,
  })
}

export function useRegImpact() {
  return useMutation({ mutationFn: copilotApi.regImpact })
}

/** F8: plan an agentic multi-step task from a natural-language goal. */
export function useAgentPlan() {
  return useMutation({ mutationFn: copilotApi.agentPlan })
}

// ── Copilot-v3 hooks ──────────────────────────────────────────────────────────

/** RPA bulk-import — extract many records from a file / pasted text. */
export function useImportRecords() {
  return useMutation({ mutationFn: copilotApi.importRecords })
}

/** Meeting notes → summary + decisions + action items + flagged risks. */
export function useMeetingActions() {
  return useMutation({ mutationFn: copilotApi.meetingActions })
}

/** Suggest a category / subcategory / tags for a free text. */
export function useCategorize() {
  return useMutation({ mutationFn: copilotApi.categorize })
}

/** Regulatory framework gap analysis (grounded on your data). */
export function useFrameworkGap() {
  return useMutation({ mutationFn: copilotApi.frameworkGap })
}

/** Auto-answer a security / due-diligence questionnaire. */
export function useAnswerQuestionnaire() {
  return useMutation({ mutationFn: copilotApi.answerQuestionnaire })
}
